import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, unref as _unref, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "text-white" }
const _hoisted_2 = { class: "flex justify-between items-center" }
const _hoisted_3 = { class: "rounded-md bg-white p-2" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "mb-1" }, "Sort by", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("hr", { class: "border-none h-[1px] bg-gray-200 my-2" }, null, -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("div", { class: "mb-1" }, "Order by", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("hr", { class: "border-none h-[1px] bg-gray-200 my-2" }, null, -1)
const _hoisted_8 = { class: "flex items-center" }
const _hoisted_9 = {
  key: 0,
  class: "mr-2 sm:mr-4 text-xl w-0.5 h-8 bg-white text-white"
}
const _hoisted_10 = { class: "bg-white rounded-md py-1 sm:py-2 px-2 sm:px-4" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("hr", { class: "mt-2 sm:mt-4" }, null, -1)
const _hoisted_12 = { class: "mt-2 sm:mt-4 -mb-6" }
const _hoisted_13 = {
  key: 0,
  style: {}
}

import { useRoute, useRouter } from 'vue-router';
import { createVNode, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { Modal, Pagination, TreeSelect, TreeSelectProps, Dropdown } from 'ant-design-vue';
import {
	PlusOutlined,
	DeleteFilled,
	EditFilled,
	EyeFilled,
	StarFilled,
	ExportOutlined
} from '@ant-design/icons-vue';
import HitomiCard from '@/components/disposable/HitomiCard.vue';
import ActionButtons from '@/components/shared/ActionButtons.vue';
import ImageLoadingCard from '@/components/shared/ImageLoadingCard.vue';
import { Metadata } from '@/model/api';
import { ActionButton } from '@/model/button';
import { Hitomi, HitomiPaginationRequest } from '@/model/hitomi';
import LocalStorageKey from '@/model/key';
import { ActionMode, DocumentViewMode, SortSequence } from '@/model/universal';
import {
	deleteHitomi,
	favoriteHitomi,
	getHitomiPagination,
	getHitomiOptions
} from '@/repository/hitomi';
import { assignWithDetail } from '@/util/universal';

enum FilterID {
	Artists = 'artists',
	Groups = 'groups',
	Tags = 'tags',
	Favorite = 'Favorite'
}

const External = 'external';

export default /*#__PURE__*/_defineComponent({
  __name: 'HitomiView',
  setup(__props) {

const store = useStore();
const route = useRoute();
const router = useRouter();
onMounted(async () => {
	const options = await getHitomiOptions();
	if (options && filters.value) {
		const option = (value: string[], color: string, id: string) =>
			value.map((v) => {
				return {
					label: v,
					value: id + ':' + v,
					color
				};
			});
		filters.value[1].children = option(options.artists, 'orange', FilterID.Artists);
		filters.value[2].children = option(options.groups, 'green', FilterID.Groups);
		filters.value[3].children = option(options.tags, 'cyan', FilterID.Tags);
	}
	await fetchData();
});

const sortOptions = [
	{
		label: 'Date',
		value: 'created_at'
	},
	{
		label: 'Title',
		value: 'hitomi_title'
	},
	{
		label: 'Artist',
		value: 'hitomi_artists.0'
	},
	{
		label: 'Group',
		value: 'hitomi_groups.0'
	},
	{
		label: 'Favorite',
		value: 'is_favorite'
	}
];
const orderOptions = ref([
	{
		label: () => {
			switch (sortBy.value) {
				case 'created_at':
					return 'First to last';
				case 'hitomi_title':
				case 'hitomi_artists.0':
				case 'hitomi_groups.0':
					return '0-9, A-Z, a-z';
				case 'is_favorite':
					return 'Favorite last';
				default:
					return '';
			}
		},
		value: SortSequence.Ascending
	},
	{
		label: () => {
			switch (sortBy.value) {
				case 'created_at':
					return 'Last to first';
				case 'hitomi_title':
				case 'hitomi_artists.0':
				case 'hitomi_groups.0':
					return 'z-a, Z-A, 9-0';
				case 'is_favorite':
					return 'Favorite first';
				default:
					return '';
			}
		},
		value: SortSequence.Descending
	}
]);
const filters = ref<TreeSelectProps['treeData']>([
	{
		label: 'Favorite',
		value: 'Favorite',
		color: 'yellow'
	},
	{
		label: 'Artists',
		value: 'Artists',
		color: 'pink',
		children: []
	},
	{
		label: 'Groups',
		value: 'Groups',
		color: 'green',
		children: []
	},
	{
		label: 'Tags',
		value: 'Tags',
		color: 'blue',
		children: []
	}
]);
const sortBy = ref(
	sortOptions.find(
		(sort) => sort.label.toLowerCase() === route.query.sort?.toString().toLowerCase()
	)?.value || sortOptions[0].value
);
const orderBy = ref(
	route.query.order &&
		orderOptions.value.map((opt) => opt.value.toString()).includes(route.query.order.toString())
		? route.query.order.toString()
		: orderOptions.value[0].value
);
const filterBy = ref<string[]>(
	(() => {
		const artists =
			route.query.artists
				?.toString()
				.split(',')
				.map((artist) => FilterID.Artists + ':' + artist) || [];
		const groups =
			route.query.groups
				?.toString()
				.split(',')
				.map((group) => FilterID.Groups + ':' + group) || [];
		const tags =
			route.query.tags
				?.toString()
				.split(',')
				.map((tag) => FilterID.Tags + ':' + tag) || [];
		const isFavorite = route.query.isFavorite ? [FilterID.Favorite] : [];
		return [...isFavorite, ...artists, ...groups, ...tags];
	})()
);
const openDropdown = ref(false);

// Action Buttons
const mode = ref(
	assignWithDetail<string>(
		[
			ActionMode.View,
			External,
			ActionMode.Favorite,
			ActionMode.Add,
			ActionMode.Edit,
			ActionMode.Delete
		],
		ActionMode.View,
		localStorage.getItem(LocalStorageKey.HitomiAction)
	)
);
const actions: ActionButton[][] = [
	[
		{
			tooltip: 'View',
			mode: ActionMode.View,
			component: EyeFilled,
			activeClass: 'bg-pink-400 !text-black',
			hoverClass: 'hover:bg-pink-400 hover:!text-black',
			action: () => (mode.value = ActionMode.View)
		},
		{
			tooltip: 'External Link',
			mode: External,
			component: ExportOutlined,
			activeClass: 'bg-blue-400 !text-black',
			hoverClass: 'hover:bg-blue-400 hover:!text-black',
			action: () => (mode.value = External)
		},
		{
			tooltip: 'Favorite',
			mode: ActionMode.Favorite,
			component: StarFilled,
			activeClass: 'bg-yellow-400 !text-red-600',
			hoverClass: 'hover:bg-yellow-400 hover:!text-red-600',
			action: () => (mode.value = ActionMode.Favorite)
		}
	],
	[
		{
			tooltip: 'Add',
			mode: ActionMode.Add,
			component: PlusOutlined,
			activeClass: 'bg-green-300 !text-black',
			hoverClass: 'hover:bg-green-300 hover:!text-black',
			action: () => router.push({ name: 'Create Hitomi' })
		},
		{
			tooltip: 'Edit',
			mode: ActionMode.Edit,
			component: EditFilled,
			activeClass: 'bg-blue-400 !text-white',
			hoverClass: 'hover:bg-blue-400 hover:!text-white',
			action: () => (mode.value = ActionMode.Edit)
		},
		{
			tooltip: 'Delete',
			mode: ActionMode.Delete,
			component: DeleteFilled,
			activeClass: 'bg-red-500 !text-white',
			hoverClass: 'hover:bg-red-500 hover:!text-white',
			action: () => (mode.value = ActionMode.Delete)
		}
	]
];
const search = ref(route.query.search?.toString() || '');
const sort = ref(`${sortBy.value} ${orderBy.value}`);
const metadata = ref<Metadata>(initialMetadata());
function initialMetadata(): Metadata {
	return {
		limit: Number(route.query.limit) || 10,
		offset: 0,
		totalItem: 0,
		totalPage: 0,
		currentPage: Number(route.query.page) || 1
	};
}

function filter(field: string) {
	if (field === FilterID.Favorite) {
		return filterBy.value.findIndex((filter) => filter === FilterID.Favorite) !== -1
			? FilterID.Favorite
			: '';
	}
	const filterValues = filterBy.value.filter((f) => f !== FilterID.Favorite);
	const filterByField =
		filters.value
			?.find((filter) => filter.label.toLowerCase() === field)
			?.children?.map((v) => v.value) || [];
	return filterByField
		.filter((filter) => filterValues.includes(filter))
		.map((filter) => filter.substring(filter.indexOf(':') + 1))
		.join(',');
}

let _firstTime = true;
function clearInvalidFilter() {
	if (_firstTime && filterBy.value && filters.value) {
		let allFilters: string[] = [];
		filters.value.forEach((filter) => {
			if (filter.children) {
				allFilters.push(...filter.children.map((f) => f.value as string));
			} else {
				allFilters.push(filter.value);
			}
		});
		filterBy.value = filterBy.value.filter((filter) => allFilters.includes(filter));
	}
	_firstTime = false;
}

const isFetching = ref(true);
let hitomi = ref<Hitomi[]>([]);
async function fetchData() {
	clearInvalidFilter();
	const advanceFilter = {
		artists: filter(FilterID.Artists) || undefined,
		groups: filter(FilterID.Groups) || undefined,
		tags: filter(FilterID.Tags) || undefined,
		isFavorite: filter(FilterID.Favorite).length > 0 || undefined
	};
	const query: HitomiPaginationRequest = {
		page: metadata.value.currentPage,
		offset: metadata.value.offset,
		limit: metadata.value.limit,
		sort: sort.value,
		search: search.value,
		artists: advanceFilter.artists,
		groups: advanceFilter.groups,
		tags: advanceFilter.tags,
		isFavorite: advanceFilter.isFavorite
	};
	router.replace({
		query: {
			page: metadata.value.currentPage,
			limit: metadata.value.limit,
			search: search.value || undefined,
			sort:
				sortOptions
					.find((s) => s.value.toLowerCase() === sort.value.split(' ')[0].toLowerCase())
					?.label?.toLowerCase() || undefined,
			order: sort.value.split(' ')[1] || undefined,
			artists: advanceFilter.artists,
			groups: advanceFilter.groups,
			tags: advanceFilter.tags,
			isFavorite: advanceFilter.isFavorite ? 'true' : undefined
		}
	});
	try {
		isFetching.value = true;
		const data = await getHitomiPagination(query);
		if (data) {
			isFetching.value = false;
			hitomi.value = data.data;
			metadata.value = data.metadata;
			if (metadata.value.totalPage > 0 && (!data.data || !data.data.length)) {
				metadata.value.currentPage = metadata.value.totalPage;
			}
		}
	} catch (error) {
		store.dispatch('alert', { type: 'error', message: 'something went wrong, please try again!' });
	}
}

function resetSort() {
	const option = sort.value.split(' ');
	sortBy.value = option[0];
	orderBy.value = option[1] as SortSequence;
}

function sortHitomi() {
	openDropdown.value = false;
	sort.value = `${sortBy.value} ${orderBy.value}`;
}

async function clickCard(hitomiData: Hitomi) {
	switch (mode.value) {
		case ActionMode.View:
			router.push(`/hitomi/${hitomiData.hitomiUUID}`);
			return;

		case External:
			window.open(hitomiData.hitomiLink, '_blank');
			return;

		case ActionMode.Favorite:
			hitomiData.isFavorite = !hitomiData.isFavorite;
			await favoriteHitomi(hitomiData.hitomiUUID as string, hitomiData.isFavorite);
			return;

		case ActionMode.Edit:
			router.push({ path: '/hitomi/update/' + hitomiData.hitomiUUID });
			return;

		case ActionMode.Delete:
			Modal.confirm({
				title: 'Delete Hitomi',
				icon: createVNode(DeleteFilled),
				content: 'Do you want to delete this hitomi right?',
				okText: 'Yes',
				cancelText: 'No',
				async onOk() {
					if (hitomiData.hitomiUUID) {
						await deleteHitomi(hitomiData.hitomiUUID);
						await fetchData();
					}
				}
			});
			return;
	}
}

function toggleSelectAllFiltering() {
	if (filters.value) {
		for (const value of filters.value.filter((f) => f.children)) {
			// is parent
			if (filterBy.value.includes(value.value) && value.children) {
				// have at least 1 of children on following parent
				if (
					value.children.filter((filter) => filterBy.value.includes(filter.value)).length ===
					value.children.length
				) {
					filterBy.value = filterBy.value.filter(
						(filter) =>
							filter !== value.value && !value.children?.map((v) => v.value).includes(filter)
					);
				} else {
					filterBy.value = Array.from(
						new Set(
							filterBy.value
								.filter((filter) => filter !== value.value)
								.concat(value.children.map((v) => v.value as string))
						).values()
					);
				}
			}
		}
	}
}

watch(
	() => metadata?.value.currentPage,
	async () => (metadata?.value.currentPage !== 0 ? await fetchData() : {})
);
watch(
	() => metadata?.value.limit,
	async () => await fetchData()
);
watch(
	() => sort?.value,
	async () => await fetchData()
);
watch(
	() => filterBy?.value,
	async () => await fetchData()
);
watch(mode, () => localStorage.setItem(LocalStorageKey.HitomiAction, mode.value));
watch(filterBy, () => toggleSelectAllFiltering());

return (_ctx: any,_cache: any) => {
  const _component_a_radio = _resolveComponent("a-radio")!
  const _component_a_radio_group = _resolveComponent("a-radio-group")!
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_input_search = _resolveComponent("a-input-search")!
  const _component_a_empty = _resolveComponent("a-empty")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_form = _resolveComponent("a-form")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(Dropdown), {
        onOpenChange: _cache[4] || (_cache[4] = (e) => (openDropdown.value = e)),
        open: openDropdown.value
      }, {
        overlay: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _hoisted_4,
            _createVNode(_component_a_radio_group, {
              value: sortBy.value,
              "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((sortBy).value = $event)),
              class: "flex flex-col"
            }, {
              default: _withCtx(() => [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(sortOptions, (sort, index) => {
                  return _createVNode(_component_a_radio, {
                    key: index,
                    value: sort.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(sort.label), 1)
                    ]),
                    _: 2
                  }, 1032, ["value"])
                }), 64))
              ]),
              _: 1
            }, 8, ["value"]),
            _hoisted_5,
            _hoisted_6,
            _createVNode(_component_a_radio_group, {
              value: orderBy.value,
              "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((orderBy).value = $event)),
              class: "flex flex-col"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderOptions.value, (order, index) => {
                  return (_openBlock(), _createBlock(_component_a_radio, {
                    key: index,
                    value: order.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(order.label()), 1)
                    ]),
                    _: 2
                  }, 1032, ["value"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["value"]),
            _hoisted_7,
            _createVNode(_component_a_button, {
              class: "mr-2",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (resetSort()))
            }, {
              default: _withCtx(() => [
                _createTextVNode("Reset")
              ]),
              _: 1
            }),
            _createVNode(_component_a_button, {
              type: "primary",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (sortHitomi()))
            }, {
              default: _withCtx(() => [
                _createTextVNode("Submit")
              ]),
              _: 1
            })
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(_component_a_button, null, {
            default: _withCtx(() => [
              _createTextVNode(" Sort ")
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["open"]),
      _createElementVNode("div", _hoisted_8, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(actions, (buttons, index) => {
          return _createElementVNode("div", {
            key: index,
            class: "flex items-center gap-2 sm:gap-4 my-2 sm:my-4"
          }, [
            _createVNode(ActionButtons, {
              actions: buttons,
              mode: mode.value,
              placement: 
							(buttonIndex) =>
								index === actions.length - 1 && buttonIndex === buttons.length - 1
									? 'topRight'
									: !index && !buttonIndex
										? 'topLeft'
										: 'top'
						
            }, null, 8, ["actions", "mode", "placement"]),
            (index !== actions.length - 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9))
              : _createCommentVNode("", true)
          ])
        }), 64))
      ])
    ]),
    _createVNode(_component_a_input_search, {
      value: search.value,
      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((search).value = $event)),
      placeholder: "Search hitomi title ...",
      "enter-button": "",
      "allow-clear": "",
      onSearch: _cache[6] || (_cache[6] = ($event: any) => (fetchData()))
    }, null, 8, ["value"]),
    _createVNode(ImageLoadingCard, {
      class: "py-2 sm:py-4",
      "is-fetching": isFetching.value,
      limit: metadata.value.limit,
      "view-mode": _unref(DocumentViewMode).Grid
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(hitomi), (item, index) => {
          return (_openBlock(), _createElementBlock("div", { key: index }, [
            _createVNode(HitomiCard, {
              hitomi: item,
              onClick: clickCard,
              mode: mode.value === External ? 'EXTERNAL' : 'INTERNAL'
            }, null, 8, ["hitomi", "mode"])
          ]))
        }), 128))
      ]),
      _: 1
    }, 8, ["is-fetching", "limit", "view-mode"]),
    (!isFetching.value && !_unref(hitomi)?.length)
      ? (_openBlock(), _createBlock(_component_a_empty, {
          key: 0,
          class: "mt-2 sm:mt-4"
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_10, [
      _createVNode(_unref(Pagination), {
        current: metadata.value.currentPage,
        "onUpdate:current": _cache[7] || (_cache[7] = ($event: any) => ((metadata.value.currentPage) = $event)),
        "page-size": metadata.value.limit,
        "onUpdate:pageSize": _cache[8] || (_cache[8] = ($event: any) => ((metadata.value.limit) = $event)),
        total: metadata.value.totalItem,
        "show-total": (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        "show-size-changer": "",
        "show-quick-jumper": "",
        onChange: _cache[9] || (_cache[9] = 
					(page, pageSize) => {
						metadata.value.currentPage = page;
						metadata.value.limit = pageSize;
					}
				)
      }, null, 8, ["current", "page-size", "total", "show-total"])
    ]),
    _hoisted_11,
    _createElementVNode("div", _hoisted_12, [
      _createVNode(_component_a_form, { layout: "horizontal" }, {
        default: _withCtx(() => [
          _createVNode(_component_a_form_item, {
            label: "Filter",
            name: "hitomiFilters"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(TreeSelect), {
                value: filterBy.value,
                "onUpdate:value": _cache[10] || (_cache[10] = ($event: any) => ((filterBy).value = $event)),
                "show-search": "",
                style: {"width":"100%"},
                "dropdown-style": { maxHeight: '400px', overflow: 'auto' },
                placeholder: "Please select filter",
                "allow-clear": "",
                multiple: "",
                "show-checked-strategy": _unref(TreeSelect).SHOW_ALL,
                "tree-default-expand-all": "",
                "tree-data": filters.value,
                "tree-node-filter-prop": "label"
              }, {
                tagRender: _withCtx(({ label, closable, onClose, option }) => [
                  _createVNode(_component_a_tag, {
                    closable: closable,
                    color: option.color,
                    style: {"margin-right":"3px"},
                    onClose: onClose
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(label) + "   ", 1)
                    ]),
                    _: 2
                  }, 1032, ["closable", "color", "onClose"])
                ]),
                title: _withCtx(({ value: val, label }) => [
                  (val === 'parent 1-1')
                    ? (_openBlock(), _createElementBlock("b", _hoisted_13, _toDisplayString(val), 1))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(_toDisplayString(label), 1)
                      ], 64))
                ]),
                _: 1
              }, 8, ["value", "show-checked-strategy", "tree-data"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ])
  ]))
}
}

})